import React, { useState, useRef, useEffect, FormEvent, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AuthContext } from '../../contexts/AuthContext';
import { ApiApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';

const getClient = () => {
  return new ApiApi(getApiConfiguration());
};

const TwoFactorVerification = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const { setUserDetails } = useContext(AuthContext);
  const navigate = useNavigate();
  const tempOtpToken = localStorage.getItem('tempOtpToken');

  useEffect(() => {
    if (!tempOtpToken) {
      navigate('/login');
    }
    // Focus first input on mount
    if (inputs.current[0]) {
      inputs.current[0].focus();
    }
  }, [navigate, tempOtpToken]);

  const handleInput = (index: number, value: string) => {
    if (value.length > 1) {
      value = value[value.length - 1];
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Move to next input if value is entered
    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
    const newCode = [...code];
    pastedData.forEach((value, index) => {
      if (index < 6) {
        newCode[index] = value;
      }
    });
    setCode(newCode);
    // Focus last filled input or first empty input
    const lastIndex = Math.min(pastedData.length - 1, 5);
    inputs.current[lastIndex]?.focus();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const otp = code.join('');
    if (!tempOtpToken) {
      setError('Session expired. Please login again.');
      navigate('/login');
      return;
    }

    const client = getClient();
    const otpRequest = {
      otp,
      tempOtpToken,
    };

    try {
      const data = await client.apiAuthVerifyOtpCreate({ otpRequest });
      setUserDetails(data);
      navigate('/');
    } catch (error: any) {
      const errorData = await error.response?.json();
      if (errorData.status === "token_expired") {
        navigate('/login');
      } else if (errorData.status === "invalid_otp") {
        setError(errorData.detail);
        // Clear inputs on error
        setCode(['', '', '', '', '', '']);
        inputs.current[0]?.focus();
      } else {
        setError(errorData.detail || errorData.status || 'An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <Shield className="h-10 w-10 text-blue-500" />
            <h1 className="text-2xl font-bold text-white ml-3">Two-Factor Authentication</h1>
          </div>

          <div className="text-center mb-8">
            <p className="text-sm text-gray-400">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Code Input */}
            <div className="flex justify-center space-x-3 mb-8">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleInput(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-xl font-bold bg-gray-800/30 border border-gray-800 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              ))}
            </div>

            {error && (
              <div className="text-sm text-red-500 bg-red-500/10 px-4 py-2 rounded-lg mb-6 text-center">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="space-y-4">
              <Button 
                className="w-full" 
                type="submit"
                disabled={isLoading || code.some(digit => !digit)}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center justify-center text-sm text-gray-400 hover:text-white w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;